import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import CheckIcon from '@material-ui/icons/Check';
import PropTypes from 'prop-types';
import React, { useEffect, useReducer } from 'react';
import { FormattedMessage } from 'react-intl';
import { connect } from 'react-redux';
import { openPage } from '@cedalo/webui/src/actions/actions';
import { Overlay } from '@cedalo/webui/src/components/HelperComponent/Overlay';
import gatewayClient from '@cedalo/webui/src/helper/GatewayClient';
import { useGraphQL } from '@cedalo/webui/src/helper/Hooks';
import { intl } from '@cedalo/webui/src/helper/IntlGlobalProvider';
import { AdminPageLayout } from '@cedalo/webui/src/layouts/AdminPageLayout';
import { hasFieldError } from '@cedalo/webui/src/pages/common';
import { Path } from '@cedalo/webui/src/helper/Path';

const workspaceShape = PropTypes.shape({
	name: PropTypes.string
});

export const UpdateWorkspaceForm = (props) => {
	const { errors, workspace, valid, disabled, pristine, onNameUpdate, onCancel, onSubmit, originalWorkspace } = props;

	const errorsMappings = {
		NAME_INVALID: intl.formatMessage({ id: 'Extensions.Admin.Workspace.errorNameInvalid' }, {}),
		NAME_IN_USE: intl.formatMessage({ id: 'Extensions.Admin.Workspace.errorNameInUse' }, {}),
		NAME_REQUIRED: intl.formatMessage({ id: 'Extensions.Admin.Workspace.errorNameRequired' }, {}),
		UNEXPECTED_ERROR: intl.formatMessage({ id: 'Extensions.Admin.Workspace.errorUnexpected' }, {})
	};
	const getError = (code) => (code ? errorsMappings[code] || errorsMappings.UNEXPECTED_ERROR : undefined);

	return (
		<form>
		<Grid container spacing={2}>
				<Grid item container spacing={8} justify="space-between">
					<Grid item>
						<Typography variant="h5" gutterBottom>
							<FormattedMessage
								id="Extensions.Admin.Workspace.update"
								defaultMessage="Update Workspace"
								values={{ name: originalWorkspace.name }}
							/>
						</Typography>
					</Grid>
					{errors.form ? (
						<Grid item>
							<Typography color="error" variant="subtitle1">
								{getError(errors.form)}
							</Typography>
						</Grid>
					) : null}
				</Grid>
				<Grid item xs={12} sm={12}>
					<TextField
						required
						variant="outlined"
						size="small"
						id="name"
						label={<FormattedMessage id="Extensions.Admin.Workspace.labelName" defaultMessage="Name" />}
						fullWidth
						error={!!errors.name}
						helperText={getError(errors.name)}
						disabled={disabled}
						value={workspace.name}
						onChange={(event) => onNameUpdate(event.target.value)}
					/>
				</Grid>

				<Grid container item spacing={2} xs={12} justify="flex-end" direction="row">
					<Grid item>
						<Button variant="contained" onClick={onCancel} disabled={disabled}>
							<FormattedMessage id="Extensions.Admin.Workspace.buttonCancel" defaultMessage="Cancel" />
						</Button>
					</Grid>
					<Grid item>
						<Button
							// className={classes.button}
							variant="contained"
							color="primary"
							onClick={onSubmit}
							disabled={pristine || disabled || !valid}
						>
							<FormattedMessage id="Extensions.Admin.Workspace.buttonSave" defaultMessage="Save" />
						</Button>
					</Grid>
				</Grid>
			</Grid>
		</form>
	);
};

UpdateWorkspaceForm.propTypes = {
	workspace: workspaceShape.isRequired,
	errors: PropTypes.shape({
		name: PropTypes.string
	}).isRequired,
	pristine: PropTypes.bool.isRequired,
	valid: PropTypes.bool.isRequired,
	disabled: PropTypes.bool.isRequired,
	onNameUpdate: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired
};

const QUERY = `
query FindWorkspace($id: ID!) {
	workspace(id: $id) {
		id
		name
		users {
			id
			username
			email
			firstName
			lastName
		}
	}
}
`;

const UPDATE_WORKSPACE_MUTATION = `
mutation UpdateWorkspace($id: ID!, $workspace: UpdateWorkspaceInput!) {
	updateWorkspace(id: $id, workspace: $workspace) {
		workspace {
			id
			name
		}
		success
		code
		fieldErrors {
			name
		}
	}
}
`;

const updateWorkspaceReducer = (state, action) => {
	switch (action.type) {
		case 'set_initial_workspace':
			return {
				...state,
				workspace: {
					name: action.data.name || ''
				},
				originalWorkspace: action.data
			};
		case 'set_name':
			return {
				...state,
				pristine: false,
				workspace: { ...state.workspace, name: action.data },
				errors: {
					...state.errors,
					name: !action.data ? 'NAME_REQUIRED' : undefined
				}
			};
		case 'save':
			return {
				...state,
				savePending: true
			};
		case 'saving_error':
			return {
				...state,
				savePending: false,
				pristine: true,
				errors: action.data
			};
		case 'saving_success':
			return {
				...state,
				savePending: false,
				saved: true,
				errors: {}
			};
		default:
			throw new Error(`Unkown action '${action.type}'`);
	}
};

const EditWorkspaceComponent = (props) => {
	const { onCancel, onSubmit, workspaceId } = props;
	const { loading, errors, data } = useGraphQL(QUERY, { id: workspaceId }, [workspaceId]);
	const [state, dispatch] = useReducer(updateWorkspaceReducer, {
		pristine: true,
		workspace: {
			name: ''
		},
		originalWorkspace: {
			name: ''
		},
		errors: {},
		savePending: false,
		saved: false
	});

	useEffect(() => {
		if (data && data.workspace) {
			dispatch({ type: 'set_initial_workspace', data: data.workspace });
		}
	}, [data]);

	useEffect(() => {
		if (state.saved) {
			setTimeout(() => {
				onSubmit(state.user);
			}, 500);
		}
	}, [state.saved]);

	if (errors) {
		return (
			<div
				style={{
					height: '100%',
					padding: '24px',
					backgroundColor: '#EEE',
					boxSizing: 'border-box',
					overflow: 'auto'
				}}
			>
				{errors.join('\n')}
			</div>
		);
	}

	const saveWorkspace = async () => {
		dispatch({ type: 'save' });
		try {
			const {
				updateWorkspace: { success, fieldErrors, code, workspace }
			} = await gatewayClient.graphql(UPDATE_WORKSPACE_MUTATION, {
				id: workspaceId,
				workspace: state.workspace
			});
			if (success) {
				dispatch({ type: 'saving_success', data: workspace });
			} else if (fieldErrors) {
				dispatch({ type: 'saving_error', data: fieldErrors });
			} else {
				dispatch({ type: 'saving_error', data: { form: code } });
			}
		} catch (error) {
			dispatch({ type: 'saving_error', data: { form: 'UNEXPECTED_ERROR' } });
			console.error(error);
		}
	};

	const hasError = hasFieldError(state.errors);
	const hasRequired = state.workspace.name;
	const isValid = !!(!hasError && hasRequired);
	const showProgress = loading || state.savePending;
	const showSuccess = state.saved;

	return (
		<AdminPageLayout page="workspaces" documentTitle={intl.formatMessage({ id: 'Administration' })}>
			<div
				style={{
					minHeight: '100%',
					padding: '24px'
				}}
			>
				<Paper style={{ padding: '32px', maxWidth: '960px', margin: 'auto', position: 'relative' }}>
					<UpdateWorkspaceForm
						workspace={state.workspace}
						originalWorkspace={state.originalWorkspace}
						pristine={state.pristine}
						errors={state.errors}
						disabled={loading || state.savePending || state.saved}
						onCancel={onCancel}
						valid={isValid}
						onSubmit={saveWorkspace}
						onNameUpdate={(value) => dispatch({ type: 'set_name', data: value })}
					/>
					{showProgress && (
						<Overlay>
							<CircularProgress style={{ width: '24px', height: '24px' }} />
						</Overlay>
					)}
					{showSuccess && (
						<Overlay>
							<CheckIcon color="primary" />
						</Overlay>
					)}
				</Paper>
			</div>
		</AdminPageLayout>
	);
};

EditWorkspaceComponent.propTypes = {
	workspaceId: PropTypes.string.isRequired,
	onCancel: PropTypes.func.isRequired,
	onSubmit: PropTypes.func.isRequired
};

const mapDispatchToProps = {
	onCancel: () => openPage(Path.workspaces()),
	onSubmit: () => openPage(Path.workspaces())
};

const mapStateToProps = (state, ownProps) => ({
	workspaceId: ownProps.match.params.workspaceId
});

export const EditWorkspacePage = connect(mapStateToProps, mapDispatchToProps)(EditWorkspaceComponent);



// WEBPACK FOOTER //
// /home/travis/build/cedalo/streamsheets-pro-features/packages/webui-extensions/src/workspace/EditWorkspacePage.js